<script setup>
import { ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import { FLOW_TEMPLATES } from '../constants/templates.js';

const props = defineProps({
  show: { type: Boolean, default: false },
  isSaving: { type: Boolean, default: false },
});

const emit = defineEmits(['close', 'create']);

const { t } = useI18n();
const dialogRef = ref(null);
const name = ref('');
const selectedTemplateId = ref('blank');

watch(
  () => props.show,
  val => {
    if (val) dialogRef.value?.open();
    else dialogRef.value?.close();
  }
);

function onClose() {
  name.value = '';
  selectedTemplateId.value = 'blank';
  emit('close');
}

function resolveFlowData(flowData) {
  return JSON.parse(JSON.stringify(flowData, (_, v) =>
    typeof v === 'string' && v.startsWith('FLOW_BUILDER.') ? t(v) : v
  ));
}

function onCreate() {
  if (!name.value.trim()) return;
  const template =
    FLOW_TEMPLATES.find(tpl => tpl.id === selectedTemplateId.value) ??
    FLOW_TEMPLATES[0];
  emit('create', {
    name: name.value.trim(),
    flow_data: resolveFlowData(template.flow_data),
    active: false,
  });
}
</script>

<template>
  <Dialog
    ref="dialogRef"
    :title="t('FLOW_BUILDER.CREATE_FLOW.TITLE')"
    :confirm-button-label="t('FLOW_BUILDER.CREATE_FLOW.CONFIRM')"
    :disable-confirm-button="!name.trim() || isSaving"
    :is-loading="isSaving"
    @confirm="onCreate"
    @close="onClose"
  >
    <div class="flex flex-col gap-5 py-2">
      <Input
        v-model="name"
        :label="t('FLOW_BUILDER.CREATE_FLOW.NAME_LABEL')"
        :placeholder="t('FLOW_BUILDER.CREATE_FLOW.NAME_PLACEHOLDER')"
        :autofocus="true"
      />

      <div class="flex flex-col gap-2">
        <label class="text-sm font-medium text-n-slate-11">{{
          t('FLOW_BUILDER.CREATE_FLOW.CHOOSE_TEMPLATE')
        }}</label>
        <div class="grid grid-cols-2 gap-3">
          <button
            v-for="template in FLOW_TEMPLATES"
            :key="template.id"
            class="flex flex-col items-start gap-1.5 py-5 px-4 rounded-2xl border border-solid text-left transition-all duration-200 hover:shadow-md bg-n-solid-1"
            :class="selectedTemplateId === template.id
              ? 'border-n-brand bg-n-brand/5'
              : 'border-n-weak hover:border-n-blue-9'"
            @click="selectedTemplateId = template.id"
          >
            <div class="flex items-center gap-2">
              <span
                class="flex items-center justify-center size-8 rounded-lg shrink-0"
                :class="selectedTemplateId === template.id ? 'bg-n-brand text-white' : 'bg-n-alpha-2 text-n-slate-11'"
              >
                <span :class="[template.icon, 'size-4']" />
              </span>
              <span class="text-sm font-semibold text-n-slate-12">{{ t(template.name) }}</span>
            </div>
            <p class="text-xs text-n-slate-9 leading-snug">{{ t(template.description) }}</p>
          </button>
        </div>
      </div>
    </div>
  </Dialog>
</template>
